import {
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { BillingPlanTier } from '@core/billing-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  DialogBodyComponent,
  DialogRef,
  HeadingComponent,
} from '@core/user-interface-legacy';

import { FeatureGuardContentComponent } from '../feature-guard-content/feature-guard-content.component';
import { FeatureGuardFacade } from '../feature-guard.facade';
import type { FeatureData } from '../feature-guard.model';
import { FeaturesGuardModel } from '../feature-guard.model';
import { FrontendOnlyGuardedFeature, GuardedFeature } from '../feature-guard.types';

type FeatureGuardDialogData = {
  availableInNewPlans?: boolean;
  availableFromPlanOverwrite?: BillingPlanTier;
} & (
  | {
      feature: GuardedFeature;
    }
  | {
      frontendFeature: FrontendOnlyGuardedFeature;
    }
);

@Component({
  selector: 'rt-feature-guard-dialog',
  templateUrl: './feature-guard-dialog.component.html',
  styleUrls: ['./feature-guard-dialog.component.less'],
  host: {
    '[attr.plan]': 'availableFromPlan()',
    '[attr.new-plans]': 'availableInNewPlans',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DialogBodyComponent,
    BadgeComponent,
    HeadingComponent,
    FeatureGuardContentComponent,
    TranslatePipe,
  ],
})
export class FeatureGuardDialogComponent implements DialogComponentI<any, any> {
  private readonly router = inject(Router);
  private readonly model = inject(FeaturesGuardModel);
  private readonly dialogRef = inject(DialogRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly guardFacade = inject(FeatureGuardFacade);

  private readonly headingTranslationKeysMap: Partial<Record<BillingPlanTier, string>> = {
    [BillingPlanTier.Start]: 'app.pricing.feature_guard.available_in_start',
    [BillingPlanTier.Grow]: 'app.pricing.feature_guard.available_in_grow',
    [BillingPlanTier.Optimize]: 'app.pricing.feature_guard.available_in_optimize',
  };

  public inputData: FeatureGuardDialogData;
  public featureData: FeatureData;
  public canUpgrade: boolean = false;
  public BillingPlanTier: typeof BillingPlanTier = BillingPlanTier;
  public availableFromPlan = signal<BillingPlanTier | null>(null);
  public availableInNewPlans = false;

  public headingTranslationKey = computed(() => {
    const plan = this.availableFromPlan();
    const key = plan && this.headingTranslationKeysMap[plan];
    return key ?? 'app.pricing.feature_guard.available_in_new_plans';
  });

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.store
      .pipe(
        select(AbilitiesSelectors.hasOne(Ability.ManageSubscription)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(enabled => {
        this.canUpgrade = enabled;
        this.cdRef.detectChanges();
      });

    if (!this.availableFromPlan() && 'feature' in this.inputData) {
      this.guardFacade
        .getPlanWhereFeatureIsAvailable(this.inputData.feature)
        .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
        .subscribe(availableFromPlan => {
          this.availableFromPlan.set(availableFromPlan);
          this.cdRef.detectChanges();
        });
    }

    if (this.featureData.analytics) {
      this.featureData.analytics.discrimination();
    }
  }

  public setDialogData(data: FeatureGuardDialogData): void {
    this.inputData = data;
    const featureName = 'feature' in data ? data.feature : data.frontendFeature;
    this.availableInNewPlans = data.availableInNewPlans || false;

    this.featureData = this.model.data[featureName];
    if (data.availableFromPlanOverwrite)
      this.availableFromPlan.set(data.availableFromPlanOverwrite);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
  public openPlanPicker(): void {
    if (this.featureData.analytics) {
      this.featureData.analytics.cta();
    }
    this.dialogRef.close();
    this.router.navigate([{ outlets: { dialog: ['order-2024', 'select-product'] } }]);
  }

  public get hasBadge(): boolean {
    return (
      this.availableFromPlan() === BillingPlanTier.Grow ||
      this.availableFromPlan() === BillingPlanTier.Optimize
    );
  }
}
